import { api } from '../api/client';

import { API_QUICK_ADD_TEMPLATES_URL } from '../utils';

/**
 * Quick add templates for one collection: a named, card-bound bundle of
 * element defaults that opens the new-item form pre-filled. Two tiers, the
 * same split as favorites and saved filters — `scope: 'user'` (personal,
 * creator-only) and `scope: 'collection'` (pinned for everyone, managed by
 * managers).
 *
 * Wire shape (keepr-api docs/SCHEMA.md § Quick add templates):
 *   { _id, collection_id, scope, userId, card_id, name,
 *     defaults: { [element]: { kind:'static', value }
 *                          | { kind:'formula', formula, blanks, transform } },
 *                          | { kind:'skip' } (copyAll profiles only) },
 *     lastItem: { matchElements: [], mine } | null,
 *     copyAll, duplicateProfile, defaultForDuplicate, … }
 *
 * A DUPLICATE PROFILE (`duplicateProfile: true`, derived by the API) is a
 * template that copies from an existing item — `{{source.*}}` or copyAll —
 * so it resolves only with a `sourceItemId` and is offered from Duplicate,
 * never as a quick add launcher (utils/duplicateProfiles.js).
 *
 * Denials follow the saved-filters 404/403 line: someone else's user-tier
 * template does not exist as far as this client is concerned, so the UI only
 * offers actions the caller's role already covers.
 */

const templateUrl = (collectionId, templateId) =>
	`${API_QUICK_ADD_TEMPLATES_URL(collectionId)}/${templateId}`;

/**
 * The collection's own templates (collection tier first, then the caller's
 * own, name-sorted within each). A sub-collection inherits none from the
 * collections above it (owner ruling 2026-10-21, KPR-1084): the API's
 * `inherited` is always empty and is not read.
 */
export async function getQuickAddTemplateLists(collectionId) {
	const { data } = await api.get(API_QUICK_ADD_TEMPLATES_URL(collectionId));
	return { templates: Array.isArray(data?.templates) ? data.templates : [] };
}

/** The collection's own templates only (what the managers edit). */
export async function getQuickAddTemplates(collectionId) {
	return (await getQuickAddTemplateLists(collectionId)).templates;
}

/**
 * Always creates in the USER tier — promotion is a separate, manage-gated
 * step. Needs write on the card; 409 on a duplicate name in the tier.
 */
export async function createQuickAddTemplate(collectionId, { name, card_id, defaults, lastItem, copyAll, tagIds }) {
	const payload = { name, card_id };
	if (defaults !== undefined) payload.defaults = defaults;
	if (lastItem !== undefined) payload.lastItem = lastItem;
	if (copyAll !== undefined) payload.copyAll = copyAll;
	// The tags the new item gets (tags T9): ids; the API judges what it adds.
	if (tagIds !== undefined) payload.tagIds = tagIds;
	const { data } = await api.post(API_QUICK_ADD_TEMPLATES_URL(collectionId), payload);
	return data;
}

/**
 * Partial update. Only the keys present are touched; `lastItem: null` clears
 * it. `card_id` and `scope` are not patchable — promote/demote are their own
 * endpoints, and a template is bound to its card for life. `tags` is retired
 * (docs/plans/tags.md §4.1): never sent, so a stored template's old list is
 * left for the owner's clean-up rather than refused on every save. `tagIds`
 * (T9) is the rebuilt tags' list: `[]` clears it.
 */
export async function updateQuickAddTemplate(collectionId, templateId, patch = {}) {
	const payload = {};
	['name', 'defaults', 'lastItem', 'copyAll', 'defaultForDuplicate', 'tagIds'].forEach(key => {
		if (patch[key] !== undefined) payload[key] = patch[key];
	});
	const { data } = await api.patch(templateUrl(collectionId, templateId), payload);
	return data;
}

export async function deleteQuickAddTemplate(collectionId, templateId) {
	await api.delete(templateUrl(collectionId, templateId));
}

/** manage — moves the template into the collection tier (creator kept as provenance). */
export async function promoteQuickAddTemplate(collectionId, templateId) {
	const { data } = await api.post(`${templateUrl(collectionId, templateId)}/promote`);
	return data;
}

/** manage — pulls it back to the user tier, owned by the CALLER. */
export async function demoteQuickAddTemplate(collectionId, templateId) {
	const { data } = await api.post(`${templateUrl(collectionId, templateId)}/demote`);
	return data;
}

/**
 * A SAVED template → `{ elements, warnings: [{ element, message }],
 * source: { _id, card_id } | null }`,
 * computed on the API under the caller's own grants. 403 when the caller
 * cannot add items of the card; 404 when the template is not theirs to see.
 * A duplicate profile needs `sourceItemId` — the item being duplicated (400
 * source_required without one, 404 source_unavailable when the caller cannot
 * read it); a plain template ignores it.
 */
export async function resolveQuickAddTemplate(collectionId, templateId, { sourceItemId } = {}) {
	const body = sourceItemId ? { sourceItemId } : undefined;
	const { data } = await api.post(`${templateUrl(collectionId, templateId)}/resolve`, body);
	return data || { elements: {}, warnings: [] };
}

/**
 * An UNSAVED body `{ card_id, defaults?, lastItem?, copyAll?, sourceItemId? }` → the same
 * resolution shape — the editor's preview. Validated exactly like a create
 * (a 400 names `defaults.<element>`); stores nothing.
 */
export async function previewQuickAddTemplate(collectionId, body) {
	const { data } = await api.post(`${API_QUICK_ADD_TEMPLATES_URL(collectionId)}/resolve`, body);
	return data || { elements: {}, warnings: [] };
}
